import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { Upload, X, Download, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// IMPORT CONFIGURATIONS — one per section
// ─────────────────────────────────────────────────────────────────
export const IMPORT_CONFIGS = {
  accounts_categories: {
    label: 'A/C Categories',
    table: 'accounts_categories',
    uniqueKey: 'name',
    columns: [
      { header: 'Category Name *', field: 'name',    required: true },
      { header: 'Type *',          field: 'type',    required: true, hint: 'in / out / both', transform: v => String(v).toLowerCase().trim() },
    ],
  },
  accounts_parties: {
    label: 'Parties',
    table: 'accounts_parties',
    uniqueKey: 'party_name',
    columns: [
      { header: 'Party Name *',  field: 'party_name',  required: true },
      { header: 'Party Type *',  field: 'party_type',  required: true, hint: 'customer / supplier / both' },
      { header: 'Contact',       field: 'contact' },
    ],
  },
  accounts_payment_modes: {
    label: 'Payment Modes',
    table: 'accounts_payment_modes',
    uniqueKey: 'name',
    columns: [
      { header: 'Payment Mode *', field: 'name', required: true },
    ],
  },


  // ── ACCOUNTS MASTER DATA ─────────────────────────────────────
  accounts_categories: {
    label: 'A/C Categories',
    table: 'accounts_categories',
    uniqueKey: 'name',
    columns: [
      { header: 'Category Name *', field: 'name',    required: true },
      { header: 'Type *',          field: 'type',    required: true, hint: 'in / out / both', transform: v => String(v).toLowerCase().trim() },
    ],
  },

  accounts_parties: {
    label: 'Parties',
    table: 'accounts_parties',
    uniqueKey: 'party_name',
    columns: [
      { header: 'Party Name *',  field: 'party_name',  required: true },
      { header: 'Party Type *',  field: 'party_type',  required: true, hint: 'customer / supplier / both' },
      { header: 'Contact',       field: 'contact' },
    ],
  },

  accounts_payment_modes: {
    label: 'Payment Modes',
    table: 'accounts_payment_modes',
    uniqueKey: 'name',
    columns: [
      { header: 'Payment Mode *', field: 'name', required: true },
    ],
  },

  // ── MASTER DATA ──────────────────────────────────────────────
  hr_staff: {
    label: 'HR Staff',
    table: 'hr_staff',
    uniqueKey: 'staff_id',
    hasIncrements: true,
    columns: [
      { header: 'Staff Name *',          field: 'staff_name',            required: true },
      { header: 'Staff ID *',            field: 'staff_id',              required: true },
      { header: 'Designation',           field: 'designation' },
      { header: 'Branch',                field: 'branch',                hint: 'EKM / TVM' },
      { header: 'Date of Birth',         field: 'dob',                   type: 'date' },
      { header: 'Personal Mobile',       field: 'personal_mobile' },
      { header: 'Office Mobile',         field: 'office_mobile' },
      { header: 'Personal Email',        field: 'personal_email' },
      { header: 'Official Email',        field: 'official_email' },
      { header: 'Joining Date',          field: 'joining_date',          type: 'date' },
      { header: 'Working Time',          field: 'working_time',          hint: '9.30am to 5.30pm' },
      { header: 'Blood Group',           field: 'blood_group' },
      { header: 'Highest Qualification', field: 'highest_qualification' },
      { header: 'Address',               field: 'address' },
      { header: 'Bank Name',             field: 'bank_name' },
      { header: 'Account No',            field: 'account_no' },
      { header: 'IFSC Code',             field: 'ifsc_code' },
      { header: 'Bank Branch',           field: 'account_branch' },
      { header: 'Offer Letter Salary',   field: 'offer_letter_salary',   type: 'number' },
      { header: 'Employee Status',       field: 'employee_status',       hint: 'Active / Resigned / On Leave' },
    ],
    incrementColumns: [
      { header: 'Staff ID *',       field: 'staff_id',  required: true, hint: 'Must match Staff ID in Sheet 1' },
      { header: 'Increment Date *', field: 'date',      required: true, type: 'date' },
      { header: 'Amount (₹) *',     field: 'amount',    required: true, type: 'number' },
      { header: 'Remarks',          field: 'remarks' },
    ],
  },

  batches: {
    label: 'Batches',
    table: 'batches',
    uniqueKey: 'batch_name',
    columns: [
      { header: 'Batch Name *',    field: 'batch_name',         required: true },
      { header: 'Discipline',      field: 'discipline',         hint: 'mechanical or civil (lowercase)' },
      { header: 'Centre',          field: 'centre',             hint: 'EKM / TVM' },
      { header: 'Handling Staff',  field: 'handling_staff_id',  from: 'hr_staff', match: 'full_name', col: 'Handling Staff' },
      { header: 'Start Date',      field: 'start_date',         type: 'date' },
      { header: 'End Date',        field: 'end_date',           type: 'date' },
      { header: 'Max Capacity',    field: 'max_capacity',       type: 'integer' },
      { header: 'Status',          field: 'status',             hint: 'ongoing / completed / upcoming' },
      { header: 'Remarks',         field: 'remarks' },
    ],
  },

  colleges: {
    label: 'Colleges',
    table: 'colleges',
    uniqueKey: 'college_name',
    columns: [
      { header: 'College Name *',  field: 'college_name',       required: true },
      { header: 'Short Code',      field: 'college_short_code' },
      { header: 'University',      field: 'university' },
      { header: 'District',        field: 'district' },
      { header: 'State',           field: 'state' },
      { header: 'Zone',            field: 'zone',               hint: 'Zone 1 / Zone 2 / Zone 3 / Zone 4 / Zone 5' },
      { header: 'Status',          field: 'current_status',     hint: 'Active / Inactive / Shutdown' },
      { header: 'Contact Person',  field: 'contact_person' },
      { header: 'Contact Number',  field: 'contact_number' },
      { header: 'Email',           field: 'email' },
    ],
  },

  course_packages: {
    label: 'Course Packages',
    table: 'course_packages',
    uniqueKey: 'course_name',
    columns: [
      { header: 'Course Name *',            field: 'course_name',             required: true },
      { header: 'Course Short Code',        field: 'course_short_code',       hint: 'e.g. PGDCEM, DCEM' },
      { header: 'Discipline',               field: 'discipline',              hint: 'mechanical / civil' },
      { header: 'Duration Value',           field: 'duration_value',          type: 'number',  hint: 'e.g. 12 or 4.5' },
      { header: 'Duration Unit',            field: 'duration_unit',           hint: 'months / weeks / days' },
      { header: 'Tuition Fees',             field: 'tuition_fees',            type: 'number' },
      { header: 'University / Board',       field: 'university_board' },
      { header: 'NDT Certificates',         field: 'num_ndt_certificates',    type: 'integer' },
      { header: 'NACTET Certificates',      field: 'num_nactet_certificates', type: 'integer' },
      { header: 'NDT Cutoff Marks',         field: 'ndt_cutoff_marks',        type: 'integer', hint: 'Default 60' },
      { header: 'NACTET Cutoff Marks',      field: 'nactet_cutoff_marks',     type: 'integer', hint: 'Default 50' },
      { header: 'Topics (comma separated)', field: '_topics_raw',             hint: 'e.g. Ultrasonic Testing, Radiographic Testing' },
    ],
    postProcess: async (row, supabaseClient) => {
      if (row._topics_raw) {
        const names = row._topics_raw.split(',').map(t => t.trim()).filter(Boolean);
        if (names.length > 0) {
          const { data } = await supabaseClient.from('topics').select('id,topic_name').in('topic_name', names);
          row.selected_topics = (data||[]).map(t => t.id);
        }
        delete row._topics_raw;
      } else { row.selected_topics = []; delete row._topics_raw; }
      row.course_fees = row.tuition_fees || 0;
      row.num_certificates = (row.num_ndt_certificates||0) + (row.num_nactet_certificates||0);
      row.is_active = true;
      if (!row.ndt_cutoff_marks)    row.ndt_cutoff_marks    = 60;
      if (!row.nactet_cutoff_marks) row.nactet_cutoff_marks = 50;
      if (!row.duration_unit)       row.duration_unit       = 'months';
      return row;
    },
  },

  topics: {
    label: 'Topics / Subjects',
    table: 'topics',
    uniqueKey: 'topic_name',
    columns: [
      { header: 'Topic Name *',   field: 'topic_name',    required: true },
      { header: 'Short Code',     field: 'short_code' },
      { header: 'Discipline',     field: 'discipline',    hint: 'mechanical / civil' },
      { header: 'Duration Days',  field: 'duration_days', type: 'number' },
      { header: 'Fees',           field: 'fees',          type: 'number' },
    ],
  },

  // ── STUDENTS ─────────────────────────────────────────────────
  students: {
    label: 'Students',
    table: 'students',
    uniqueKey: 'register_number',
    columns: [
      { header: 'Full Name *',        field: 'full_name',              required: true },
      { header: 'Register Number *',  field: 'register_number',        required: true },
      { header: 'Batch Name',         field: 'batch_name',             hint: 'e.g. EM47, EC13' },
      { header: 'Course Name',        field: 'course_name',            hint: 'e.g. PGDCEM-Mechanical' },
      { header: 'College Name',       field: 'college_name',           hint: 'Full college name' },
      { header: 'Academic Qualification', field: 'academic_qualification',  hint: 'e.g. B.Tech, Diploma, ITI' },
      { header: 'Discipline',         field: 'discipline',             hint: 'mechanical / civil' },
      { header: 'Centre',             field: 'centre',                 hint: 'EKM / TVM' },
      { header: 'Date of Birth',      field: 'dob',                    type: 'date' },
      { header: 'Contact Number',     field: 'contact_number' },
      { header: 'WhatsApp Number',    field: 'whatsapp_number' },
      { header: 'Email',              field: 'email' },
      { header: 'Guardian Name',      field: 'guardian_name' },
      { header: 'Guardian Contact',   field: 'guardian_contact' },
      { header: 'Place',              field: 'place' },
      { header: 'Pincode',            field: 'pincode' },
      { header: 'District',           field: 'district' },
      { header: 'State',              field: 'state' },
      { header: 'Training Timing',    field: 'training_timing' },
      { header: 'Training Mode',      field: 'training_mode',          hint: 'Offline / Online' },
      { header: 'Student Status',     field: 'student_status',         hint: 'Continuing / Discontinued / Passed Out' },
      { header: 'Joining Date',       field: 'joining_date',           type: 'date' },
      { header: 'Registration Date',  field: 'registration_date',      type: 'date' },
      { header: 'NACTET Reg No',      field: 'nactet_register_number' },
      { header: 'Hostel Needed',      field: 'hostel_needed',          type: 'bool', hint: 'yes / no' },
      { header: 'Remarks',            field: 'remarks' },
    ],
  },

  // ── FEES ─────────────────────────────────────────────────────
  fee_payments: {
    label: 'Fee Payments',
    table: 'fee_payments',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
    ],
    columns: [
      { header: 'Register Number *', field: '_register_number', required: true, hint: 'Must match Students' },
      { header: 'Payment Date *',    field: 'payment_date',     required: true, type: 'date' },
      { header: 'Amount *',          field: 'amount',           required: true, type: 'number' },
      { header: 'Payment Mode',      field: 'payment_mode',     hint: 'Cash / UPI / Bank Transfer / Cheque' },
      { header: 'Receipt Number',    field: 'receipt_number' },
      { header: 'Fee Type',          field: 'fee_type',         hint: 'Course Fee / Hostel Fee / Exam Fee / Other' },
      { header: 'Remarks',           field: 'remarks' },
    ],
  },

  // ── ATTENDANCE ───────────────────────────────────────────────
  attendance: {
    label: 'Attendance',
    table: 'attendance',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
      { field: 'topic_id',   from: 'topics',   match: 'topic_name',      col: 'Topic Name' },
    ],
    columns: [
      { header: 'Register Number *', field: '_register_number', required: true },
      { header: 'Date *',            field: 'date',             required: true, type: 'date' },
      { header: 'Topic Name',        field: '_topic_name' },
      { header: 'Session 1',         field: 'session1',         hint: 'P / A' },
      { header: 'Session 2',         field: 'session2',         hint: 'P / A' },
      { header: 'Session 3',         field: 'session3',         hint: 'P / A' },
      { header: 'Session 4',         field: 'session4',         hint: 'P / A' },
      { header: 'Remarks',           field: 'remarks' },
    ],
  },

  // ── ASSIGNMENTS ──────────────────────────────────────────────
  assignments: {
    label: 'Assignments',
    table: 'student_assignments',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
      { field: 'topic_id',   from: 'topics',   match: 'topic_name',      col: 'Topic Name' },
    ],
    columns: [
      { header: 'Register Number *',   field: '_register_number', required: true },
      { header: 'Topic Name *',        field: '_topic_name',      required: true },
      { header: 'Assignment Title',    field: 'assignment_title' },
      { header: 'Submitted Date',      field: 'submitted_date',  type: 'date' },
      { header: 'Status',              field: 'status',          hint: 'pending / corrected / resubmission' },
      { header: 'Returned Date',       field: 'returned_date',   type: 'date' },
      { header: 'Score',               field: 'score',           type: 'number' },
      { header: 'Faculty Name',        field: 'faculty_name' },
      { header: 'Remarks',             field: 'remarks' },
    ],
  },

  // ── INTERNSHIP ───────────────────────────────────────────────
  internships: {
    label: 'Internship',
    table: 'internships',
    uniqueKey: null,
    columns: [
      { header: 'Student Name *',   field: 'student_name',  required: true },
      { header: 'Contact Number',   field: 'contact_number' },
      { header: 'College Name',     field: 'college_name' },
      { header: 'Stream',           field: 'stream',        hint: 'Civil / Mechanical / Other' },
      { header: 'Centre',           field: 'centre',        hint: 'EKM / TVM' },
      { header: 'Semester',         field: 'semester',      hint: 'S6 / S7 / S8' },
      { header: 'Passedout Year',   field: 'passedout_year' },
      { header: 'Start Date',       field: 'start_date',    type: 'date' },
      { header: 'End Date',         field: 'end_date',      type: 'date' },
      { header: 'Demo Given',       field: 'demo_given',    type: 'bool', hint: 'yes / no' },
      { header: 'Hostel Provided',  field: 'hostel',        type: 'bool', hint: 'yes / no' },
      { header: 'Admission Taken',  field: 'admission_taken', type: 'bool', hint: 'yes / no' },
      { header: 'Trainer Name',     field: 'trainer_name' },
      { header: 'Batch Name',       field: 'batch_name' },
      { header: 'Topics Covered',   field: 'topics_covered' },
      { header: 'Demo Provided By', field: 'demo_provided_by' },
      { header: 'Certificate Collected Date', field: 'certificate_collected_date', type: 'date' },
      { header: 'Remarks',          field: 'remarks' },
    ],
  },

  // ── PLACEMENT — Companies ────────────────────────────────────
  placement_companies: {
    label: 'Placement Companies',
    table: 'placement_companies',
    uniqueKey: 'company_name',
    columns: [
      { header: 'Company Name *',   field: 'company_name',  required: true },
      { header: 'Website',          field: 'website' },
      { header: 'Sector',           field: 'sector' },
      { header: 'State',            field: 'state' },
      { header: 'Country',          field: 'country' },
      { header: 'Coordinator Name', field: 'coordinator_name' },
      { header: 'Phone',            field: 'phone' },
      { header: 'Email',            field: 'email' },
      { header: 'Remarks',          field: 'remarks' },
    ],
  },

  // ── PLACEMENT — HR Status ────────────────────────────────────
  hr_status: {
    label: 'Placement HR Status',
    table: 'hr_status',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
    ],
    columns: [
      { header: 'Register Number *',  field: '_register_number', required: true },
      { header: 'Backpaper Status',   field: 'backpaper_status',  hint: 'yes / no' },
      { header: 'Resume Submitted',   field: 'resume_submitted',  hint: 'yes / no' },
      { header: 'Field of Interest',  field: 'field_of_interest' },
      { header: 'English Score',      field: 'english_score',    type: 'number' },
      { header: 'Hindi Score',        field: 'hindi_score',      type: 'number' },
      { header: 'Current Status',       field: 'current_status',       hint: 'Interested / Not Interested / Placed' },
      { header: 'Reason',               field: 'reason' },
      { header: 'Latest Comment',       field: 'latest_comment' },
      { header: 'Backpaper Clear Time', field: 'backpaper_clear_time', hint: 'Less than 6 months / Less than 1 year / Less than 1.5 years / Less than 2 years / More than 2 years' },
      { header: 'Previous Experience',  field: 'previous_experience' },
    ],
  },

  // ── PLACEMENT — Set Placement ──────────────────────────────────
  placements: {
    label: 'Set Placement',
    table: 'placements',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
    ],
    columns: [
      { header: 'Register Number *', field: '_register_number', required: true },
      { header: 'Company Name *',    field: 'company_name',     required: true },
      { header: 'Job Role',          field: 'job_role' },
      { header: 'Job Location',      field: 'job_location' },
      { header: 'Offered Salary',    field: 'offered_salary',   type: 'number' },
      { header: 'Joining Date',      field: 'joining_date',     type: 'date' },
      { header: 'Sector',            field: 'sector' },
      { header: 'Project Name',      field: 'project_name' },
      { header: 'Benefits',          field: 'benefits' },
      { header: 'Job Ref No',        field: 'job_ref_no' },
      { header: 'Has Arrears',       field: 'has_arrears',      hint: 'yes / no' },
      { header: 'Joined Confirmed',  field: 'joined_confirmed', hint: 'yes / no' },
      { header: 'Placement No',         field: 'placement_no',         type: 'number' },
      { header: 'CV Status',             field: 'cv_status' },
      { header: 'Arrear Details',        field: 'arrear_details' },
      { header: 'Resigned',              field: 'resigned',             hint: 'yes / no' },
      { header: 'Resignation Date',      field: 'resignation_date',     type: 'date' },
      { header: 'Resignation Reason',    field: 'resignation_reason' },
      { header: 'Current Company',       field: 'current_company' },
      { header: 'Current Designation',   field: 'current_designation' },
      { header: 'Current Location',      field: 'current_location' },
      { header: 'Working Area',          field: 'working_area' },
      { header: 'Future Job Needed',     field: 'future_job_needed',    hint: 'yes / no' },
    ],
  },
  // ── PLACEMENT — After Placement ──────────────────────────────
  after_placement: {
    label: 'After Armstech Placement',
    table: 'after_placement',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
    ],
    columns: [
      { header: 'Register Number *',          field: '_register_number',          required: true },
      { header: 'Current Company',            field: 'current_company' },
      { header: 'Job Role',                   field: 'job_role' },
      { header: 'Working Area',               field: 'working_area' },
      { header: 'Job Location',               field: 'job_location' },
      { header: 'Current Salary',             field: 'current_salary',            type: 'number' },
      { header: 'Experience Years',           field: 'experience_years',          type: 'number' },
      { header: 'Current Contact',            field: 'current_contact' },
      { header: 'Client Name',                field: 'client_name' },
      { header: 'Project Details',            field: 'project_details' },
      { header: 'Additional Qualification',   field: 'additional_qualification' },
      { header: 'Placement Assistance',       field: 'placement_assistance',      hint: 'yes / no' },
      { header: 'Armstech Action',            field: 'armstech_action' },
      { header: 'Feedback',                   field: 'feedback' },
      { header: 'Contacted Date',             field: '_contact_date',        hint: 'YYYY-MM-DD' },
      { header: 'Contact Response',           field: '_contact_response',    hint: 'Student response text' },
    ],
  },
  // ── PLACEMENT — Job Offers ───────────────────────────────────
  placement_offers: {
    label: 'Placement Job Offers',
    table: 'placement_offers',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
    ],
    columns: [
      { header: 'Register Number *', field: '_register_number', required: true },
      { header: 'Company Name *',    field: 'company_name',    required: true },
      { header: 'Job Role',          field: 'job_role' },
      { header: 'Job Location',      field: 'job_location' },
      { header: 'Offered Salary',    field: 'offered_salary',  type: 'number' },
      { header: 'Offer Date',        field: 'offer_date',      type: 'date' },
      { header: 'Student Status',    field: 'student_status',  hint: 'Accepted / Rejected / Pending' },
      { header: 'Client Status',     field: 'client_status',   hint: 'Selected / Rejected / On Hold' },
      { header: 'Remarks',           field: 'remarks' },
      { header: 'Rejection Reason',  field: 'rejection_reason' },
    ],
  },

  // ── LANGUAGE TRAINING ────────────────────────────────────────
  language_sessions: {
    label: 'Language Training',
    table: 'language_sessions',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
    ],
    columns: [
      { header: 'Register Number *', field: '_register_number', required: true },
      { header: 'Session Date *',    field: 'session_date',     required: true, type: 'date' },
      { header: 'Language *',        field: 'language',         required: true, hint: 'english / hindi / arabic' },
      { header: 'Attended',          field: 'attended',         hint: 'yes / no' },
      { header: 'Topic',             field: 'topic' },
      { header: 'Trainer Name',      field: 'trainer_name' },
      { header: 'Remarks',           field: 'remarks' },
    ],
  },

  // ── STUDENT REQUESTS ─────────────────────────────────────────
  student_requests: {
    label: 'Student Requests',
    table: 'student_requests',
    uniqueKey: null,
    lookups: [
      { field: 'student_id', from: 'students', match: 'register_number', col: 'Register Number' },
    ],
    columns: [
      { header: 'Register Number *', field: '_register_number', required: true },
      { header: 'Request Date *',    field: 'request_date',     required: true, type: 'date' },
      { header: 'Request Type *',    field: 'request_type',     required: true },
      { header: 'Request Mode',      field: 'request_mode' },
      { header: 'Description',       field: 'description' },
      { header: 'Status',            field: 'status',           hint: 'pending / Approved / Processed' },
      { header: 'Concerned Person',  field: 'concerned_person' },
      { header: 'Action Date',       field: 'action_date',      type: 'date' },
      { header: 'Remarks',           field: 'remarks' },
    ],
  },

  // ── NACTET DEMO FEEDBACK ─────────────────────────────────────
  nactet_demo_feedback: {
    label: 'NACTET Demo Feedback',
    table: 'nactet_demo_feedback',
    uniqueKey: null,
    columns: [
      { header: 'Enquiry Date',      field: 'enquiry_date',     type: 'date' },
      { header: 'Student Name *',    field: 'student_name',     required: true },
      { header: 'College Name',      field: 'college_name' },
      { header: 'Stream',            field: 'stream' },
      { header: 'Phone No',          field: 'phone_no' },
      { header: 'Email ID',          field: 'email_id' },
      { header: 'Enquiry Type',      field: 'enquiry_type' },
      { header: 'Demo Type',         field: 'demo_type' },
      { header: 'Demo By',           field: 'demo_by' },
      { header: 'Coordinator',       field: 'coordinator' },
      { header: 'Demo Date',         field: 'demo_date',        type: 'date' },
      { header: 'Session Time',      field: 'session_time' },
      { header: 'Attended',          field: 'attended' },
      { header: 'Review',            field: 'review' },
      { header: 'Final Status',      field: 'final_status' },
      { header: 'Followup Date',     field: 'followup_date',    type: 'date' },
      { header: 'Admission Status',  field: 'admission_status' },
      { header: 'Centre',            field: 'center' },
      { header: 'Batch',             field: 'batch' },
      { header: 'Package',           field: 'package_name' },
      { header: 'Reg Fee Paid',      field: 'reg_fee_paid',     type: 'number' },
      { header: 'Paid Date',         field: 'paid_date',        type: 'date' },
      { header: 'Payment Ref No',    field: 'payment_ref_no' },
      { header: 'Docs Received',     field: 'docs_received' },
      { header: 'Group',             field: 'group_name' },
      { header: 'Hostel',            field: 'hostel' },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────
// SAMPLE EXCEL GENERATOR
// ─────────────────────────────────────────────────────────────────
function generateSampleExcel(config) {
  if (!config || !config.columns) { alert('No template config found'); return; }

  const sampleData = {};

  const headers = config.columns.map(c => c.header);
  const hints   = config.columns.map(c => c.hint || (c.type==='date'?'YYYY-MM-DD':c.type==='bool'?'yes / no':c.type==='number'?'Number only':''));
  const samples = sampleData[config.table] || [config.columns.map(c => c.type==='number'?'0':c.type==='date'?'2025-01-01':c.required?`Sample ${c.header.replace(' *','')}`:'')];

  try {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Staff Details
    const ws1 = XLSX.utils.aoa_to_sheet([headers, hints, ...samples]);
    ws1['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 4, 18) }));
    XLSX.utils.book_append_sheet(wb, ws1, config.label);

    // Sheet 2: Increments (hr_staff only)
    if (config.hasIncrements && config.incrementColumns) {
      const incHeaders = config.incrementColumns.map(c => c.header);
      const incHints   = config.incrementColumns.map(c => c.hint||(c.type==='date'?'YYYY-MM-DD':c.type==='number'?'Number only':''));
      const incSamples = [
        ['EMP001','2023-06-01','2000','Annual increment'],
        ['EMP001','2024-06-01','3000','Performance bonus'],
        ['EMP002','2023-07-01','5000','Promotion increment'],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet([incHeaders, incHints, ...incSamples]);
      ws2['!cols'] = incHeaders.map(h => ({ wch: Math.max(h.length+4, 18) }));
      XLSX.utils.book_append_sheet(wb, ws2, 'Increments');
    }

    XLSX.writeFile(wb, `${config.label.replace(/ /g,'_')}_Template.xlsx`);
  } catch(e) {
    const rows = [headers, hints, ...samples];
    const csv  = rows.map(r => r.map(v => `"${String(v||'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href=url; a.download=`${config.label.replace(/ /g,'_')}_Template.csv`;
    a.click(); URL.revokeObjectURL(url);
  }
}
// ─────────────────────────────────────────────────────────────────
// VALUE CONVERTERS
// ─────────────────────────────────────────────────────────────────
function convertValue(raw, type) {
  if (raw === null || raw === undefined || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (type === 'number')  { const n = parseFloat(s.replace(/[^0-9.-]/g,'')); return isNaN(n) ? null : n; }
  if (type === 'integer') { const n = parseInt(s.replace(/[^0-9.-]/g,'')); return isNaN(n) ? null : n; }
  if (type === 'bool')   { return ['yes','true','1','y'].includes(s.toLowerCase()); }
  if (type === 'date')   {
    // Already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0,10);

    // DD-MM-YYYY or DD/MM/YYYY
    if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(s)) {
      const parts = s.split(/[-/]/);
      const d = parseInt(parts[0]), m = parseInt(parts[1]), y = parseInt(parts[2]);
      // Validate: month must be 1-12, day must be 1-31
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      }
      // If month > 12, try MM-DD-YYYY interpretation
      if (d >= 1 && d <= 12 && m >= 1 && m <= 31) {
        return `${y}-${String(d).padStart(2,'0')}-${String(m).padStart(2,'0')}`;
      }
      // Both interpretations invalid — skip this date
      return null;
    }

    // Excel serial number — Excel epoch is Dec 30, 1899
    const n = Number(s);
    if (!isNaN(n) && n > 0 && n < 100000) {
      try {
        const d = new Date(Date.UTC(1899, 11, 30) + Math.round(n) * 86400000);
        if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      } catch(e) {}
    }
    return s || null;
  }
  return s;
}

// ─────────────────────────────────────────────────────────────────
// MAIN ImportButton COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function ImportButton({ configKey, onImported }) {
  const config = IMPORT_CONFIGS[configKey];
  const fileRef = useRef();

  const [showModal, setShowModal]   = useState(false);
  const [step, setStep]             = useState('upload');   // upload | preview | result
  const [rows, setRows]             = useState([]);         // parsed rows
  const [duplicates, setDuplicates] = useState([]);         // rows that already exist
  const [errors, setErrors]         = useState([]);         // validation errors
  const [importing, setImporting]   = useState(false);
  const [result, setResult]         = useState(null);       // { inserted, updated, skipped }
  const [dupChoice, setDupChoice]   = useState('skip');     // 'skip' | 'update'
  const [lookupMaps, setLookupMaps] = useState({});
  const [incrementData, setIncrementData] = useState([]);         // { field: { name: id } }

  function openModal() { resetState(); setShowModal(true); }
  function resetState() { setStep('upload'); setRows([]); setDuplicates([]); setErrors([]); setResult(null); setDupChoice('skip'); setLookupMaps({}); setIncrementData([]); }

  // ── Step 1: Parse uploaded Excel/CSV ──────────────────────────
  async function handleFile(e) {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      toast.loading('Reading file...', { id: 'parse' });

      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf, { type:'array', cellDates:false });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header:1, defval:'' });

      toast.dismiss('parse');

      if (!raw || raw.length < 1) {
        toast.error('File appears empty');
        return;
      }

      // Read Increments sheet if present (hr_staff only)
      let incrementRows = [];
      if (config.hasIncrements && wb.SheetNames.length > 1) {
        try {
          const wsInc = wb.Sheets[wb.SheetNames[1]];
          if (wsInc) {
            const rawInc = XLSX.utils.sheet_to_json(wsInc, { header:1, defval:'' });
            if (rawInc.length > 1) {
              let incHeaderIdx = 0;
              for (let i = 0; i < Math.min(5, rawInc.length); i++) {
                const rowStr = rawInc[i].map(v => String(v||'').toLowerCase()).join(' ');
                if (rowStr.includes('staff') || rowStr.includes('increment')) { incHeaderIdx = i; break; }
              }
              const incHeaders = rawInc[incHeaderIdx].map(v => String(v||'').trim().replace(' *','').toLowerCase());
              rawInc.slice(incHeaderIdx + 1).forEach(row => {
                if (!row.some(v => v !== '' && v !== null)) return;
                const inc = {};
                incHeaders.forEach((h, i) => { inc[h] = String(row[i]||'').trim(); });
                const sid = (inc['staff id'] || inc['staff_id'] || '').trim();
                const date = (inc['increment date'] || inc['date'] || '').trim();
                const amt  = (inc['amount (₹)'] || inc['amount'] || '').trim();
                const rmk  = (inc['remarks'] || '').trim();
                if (sid && !sid.toLowerCase().includes('must')) incrementRows.push({ staff_id:sid, date, amount:amt, remarks:rmk });
              });
            }
          }
        } catch(incErr) { console.error('Increment sheet error:', incErr); }
      }
      setIncrementData(incrementRows);

      // Find header row — first row containing a required column
      let headerRowIdx = 0;
      const reqHeaders = config.columns.filter(c=>c.required).map(c=>c.header.replace(' *','').toLowerCase().trim());
      for (let i = 0; i < Math.min(5, raw.length); i++) {
        const rowLower = raw[i].map(v => String(v||'').toLowerCase().replace(' *','').trim());
        if (reqHeaders.some(h => rowLower.includes(h))) { headerRowIdx = i; break; }
      }

      const headerRow = raw[headerRowIdx].map(v => String(v||'').trim().replace(' *',''));

      // Data starts right after header row - NO hints row skipping
      const dataStart = headerRowIdx + 1;

      const dataRows = raw.slice(dataStart).filter(r => r.some(v => v !== '' && v !== null));

      if (dataRows.length === 0) {
        toast.error('No data rows found. Make sure data starts from Row 3.');
        return;
      }

      // Parse rows
      const parsedRows = [];
      const parseErrors = [];

      dataRows.forEach((rawRow, ri) => {
        const row = {};
        let hasRequiredMissing = false;

        config.columns.forEach(col => {
          const cleanTarget = col.header.replace(' *','').toLowerCase().trim();
          const idx = headerRow.findIndex(h => {
            const cleanH = h.replace(' *','').toLowerCase().trim();
            return cleanH === cleanTarget ||
                   cleanH.replace(/\s+/g,'') === cleanTarget.replace(/\s+/g,'') ||
                   cleanH.includes(cleanTarget) || cleanTarget.includes(cleanH);
          });
          const rawVal = idx >= 0 ? rawRow[idx] : '';
          const val    = convertValue(rawVal, col.type);
          if (col.required && (val === null || val === '')) {
            parseErrors.push(`Row ${dataStart + ri + 1}: "${col.header}" is required but empty`);
            hasRequiredMissing = true;
          }
          row[col.field] = val;
        });

        if (!hasRequiredMissing) parsedRows.push(row);
      });

      // Strip fields not in DB
      const STRIP_FIELDS = {
        colleges: ['location','is_active'],
        batches:  ['batch_code'],
        };
      const stripFields = STRIP_FIELDS[config.table] || [];
      let cleanedRows = parsedRows.map(row => {
        const r = { ...row };
        stripFields.forEach(f => delete r[f]);
        return r;
      });

      setErrors(parseErrors);

      if (cleanedRows.length === 0) {
        const msg = parseErrors.length > 0
          ? `All ${parseErrors.length} rows failed. Check errors below.`
          : 'No valid rows found. Check your Excel format matches the template.';
        toast.error(msg);
        setStep('preview'); // show errors
        setRows([]);
        return;
      }

      // Check duplicates
      let dups = [];
      if (config.uniqueKey) {
        try {
          const keys = cleanedRows.map(r => r[config.uniqueKey]).filter(Boolean);
          if (keys.length > 0) {
            const { data: existing } = await supabase
              .from(config.table)
              .select(config.uniqueKey)
              .in(config.uniqueKey, keys);
            const existingSet = new Set((existing||[]).map(r => String(r[config.uniqueKey]).toLowerCase()));
            dups = cleanedRows.filter(r => existingSet.has(String(r[config.uniqueKey]||'').toLowerCase()));
          }
        } catch(dupErr) { console.error('Dup check error:', dupErr); }
      }

      // Fetch lookup maps (register_number → student_id etc.)
      if (config.lookups && config.lookups.length > 0) {
        const maps = {};
        await Promise.all(config.lookups.map(async lk => {
          const { data } = await supabase.from(lk.from).select(`id, ${lk.match}`);
          if (data) {
            maps[lk.field] = {};
            data.forEach(row => {
              if (row[lk.match]) maps[lk.field][String(row[lk.match]).toLowerCase()] = row.id;
            });
          }
        }));
        setLookupMaps(maps);
        // Re-resolve all rows with the new maps
        cleanedRows = cleanedRows.map(row => {
          const resolved = { ...row };
          config.lookups.forEach(lk => {
            const col = config.columns.find(c => c.field.startsWith('_'));
            const nameVal = col ? String(row[col.field]||'').toLowerCase() : '';
            if (nameVal && maps[lk.field]) {
              resolved[lk.field] = maps[lk.field][nameVal] || null;
            }
          });
          return resolved;
        });
      }
      setRows(cleanedRows);
      setDuplicates(dups);
      setStep('preview');
      if (fileRef.current) fileRef.current.value = '';

    } catch(err) {
      toast.dismiss('parse');
      console.error('Import parse error:', err);
      toast.error('Error reading file: ' + err.message);
    }
  }

  // ── Resolve FK lookups ─────────────────────────────────────────
  function resolveRow(row) {
    const resolved = { ...row };
    // Remove lookup helper fields and resolve FK IDs
    if (config.lookups) {
      config.lookups.forEach(lk => {
        const helperField = `_${lk.match.split('_')[0]}_name` in row ? `_${lk.match.split('_')[0]}_name` : null;
        // Find the column that maps to this lookup
        const col = config.columns.find(c => c.field.startsWith('_') && lk.col.toLowerCase().includes(c.field.replace('_','').replace('_name','').replace('_',' ')));
        const nameVal = col ? String(row[col.field]||'').toLowerCase() : '';
        if (nameVal && lookupMaps[lk.field]) {
          resolved[lk.field] = lookupMaps[lk.field][nameVal] || null;
        }
        // Remove helper _ fields
        config.columns.filter(c=>c.field.startsWith('_')).forEach(c=>{ delete resolved[c.field]; });
      });
    }
    return resolved;
  }

  // ── Step 2: Import ─────────────────────────────────────────────
  async function runImport() {
    setImporting(true);
    const dupKeys = new Set(duplicates.map(r=>String(r[config.uniqueKey]||'').toLowerCase()));
    const toInsert = rows.filter(r=>!dupKeys.has(String(r[config.uniqueKey]||'').toLowerCase())).map(resolveRow);
    const toUpdate = dupChoice === 'update' ? duplicates.map(resolveRow) : [];
    const toSkip   = dupChoice === 'skip'   ? duplicates : [];

    let inserted=0, updated=0, skipped=toSkip.length, errors=[];

    // Get valid DB fields from config (exclude helper _ fields)
    const validFields = new Set(
      config.columns
        .filter(c => !c.field.startsWith('_'))
        .map(c => c.field)
    );
    // Also include FK lookup fields from lookups config
    if (config.lookups) config.lookups.forEach(lk => validFields.add(lk.field));

    // ALWAYS known-bad fields to strip for each table
    const knownBadFields = {
      colleges: ['location', 'is_active'],
      batches:  ['batch_code'],
    };

    // Known integer DB columns - floats not allowed
    const INTEGER_COLS = new Set([
      'max_capacity',
      'num_ndt_certificates','num_nactet_certificates','num_certificates',
      'ndt_cutoff_marks','nactet_cutoff_marks',
      'experience_years','experience_months',
    ]);

    // Strip any fields not in validFields to prevent schema errors from old Excel files
    const cleanRow = (row) => {
      const cleaned = {};
      for (const [k, v] of Object.entries(row)) {
        if (!validFields.has(k)) continue;
        // Force integer for known integer columns
        if (INTEGER_COLS.has(k) && v !== null && v !== undefined && v !== '') {
          cleaned[k] = parseInt(v) || 0;
        } else {
          cleaned[k] = v;
        }
      }
      // Strip known-bad fields for this table
      const bad = knownBadFields[config.table] || [];
      bad.forEach(f => { delete cleaned[f]; });
      return cleaned;
    };

    // Insert new records in chunks
    for (let i=0; i<toInsert.length; i+=200) {
      const rawChunk = toInsert.slice(i,i+200);
      const chunk = await Promise.all(rawChunk.map(async (row, idx) => {
        let r = cleanRow(row);
        // For batches: always generate a fresh unique batch_code
        if (config.table === 'batches') {
          delete r.batch_code;
          r.batch_code = `BATCH-${Date.now().toString().slice(-6)}-${idx}-${Math.floor(Math.random()*9999)}`;
        }
        // For hr_staff: attach increments from Sheet 2
        if (config.table === 'hr_staff') {
          if (incrementData.length > 0) {
            const rowId = String(r.staff_id||'').trim().toLowerCase();
            const staffIncs = incrementData
              .filter(inc => String(inc.staff_id||'').trim().toLowerCase() === rowId)
              .map(inc => ({ date: convertValue(inc.date,'date'), amount: inc.amount ? Number(String(inc.amount).replace(/[^0-9.-]/g,'')) : null, remarks: inc.remarks||'' }))
              .filter(inc => inc.date && inc.amount);
            if (staffIncs.length > 0) r.increments = staffIncs;
            else r.increments = r.increments || [];
          } else { r.increments = r.increments || []; }
        }
        // Run postProcess if defined (e.g. resolve topic names for course_packages)
        if (config.postProcess) {
          r = await config.postProcess(r, supabase);
        }
        return r;
      }));

      // Final sanitize: force parseInt on integers, normalize constrained fields
      const sanitized = chunk.map(row => {
        const r = { ...row };
        Object.keys(r).forEach(k => {
          if (INTEGER_COLS.has(k) && typeof r[k] === 'number' && !Number.isInteger(r[k])) {
            r[k] = Math.round(r[k]);
          }
          if (INTEGER_COLS.has(k) && typeof r[k] === 'string' && r[k].includes('.')) {
            r[k] = Math.round(parseFloat(r[k])) || 0;
          }
        });
        // Set is_active for students
        if (config.table === 'students') r.is_active = true;
        // Normalize discipline to lowercase
        if (r.discipline) r.discipline = String(r.discipline).toLowerCase().trim();
        // Normalize student_status
        if (r.student_status) {
          const s = String(r.student_status).trim().toLowerCase();
          if (s.includes('pass')) r.student_status = 'Passed Out';
          else if (s.includes('discon')) r.student_status = 'Discontinued';
          else r.student_status = 'Continuing';
        }
        // Normalize training_mode
        if (r.training_mode) {
          const m = String(r.training_mode).trim().toLowerCase();
          r.training_mode = m.includes('online') ? 'Online' : 'Offline';
        }
        // Normalize hostel_needed boolean
        if (r.hostel_needed !== undefined && r.hostel_needed !== null) {
          r.hostel_needed = ['true','yes','1','y'].includes(String(r.hostel_needed).toLowerCase()) ? true : false;
        }
        return r;
      });
      // Use upsert for hr_staff to avoid duplicate staff_id errors
      const insertFn = config.table === 'hr_staff'
        ? supabase.from(config.table).upsert(sanitized, { onConflict: 'staff_id' })
        : config.table === 'students'
        ? supabase.from(config.table).upsert(sanitized, { onConflict: 'register_number' })
        : supabase.from(config.table).insert(sanitized);
      const {error} = await insertFn;
      if (error) errors.push(error.message);
      else inserted += chunk.length;
    }

    // Update existing records
    for (const row of toUpdate) {
      // Don't update batch_code — it's immutable after creation
      let updateRow = cleanRow(row);
      if (config.table === 'batches') delete updateRow.batch_code;
      // Sanitize integer columns before update
      Object.keys(updateRow).forEach(k => {
        if (INTEGER_COLS.has(k) && updateRow[k] !== null && updateRow[k] !== undefined) {
          updateRow[k] = Math.round(parseFloat(updateRow[k])) || 0;
        }
      });
      const {error} = await supabase.from(config.table).update(updateRow).eq(config.uniqueKey, row[config.uniqueKey]);
      if (error) errors.push(error.message);
      else updated++;
    }

    setResult({ inserted, updated, skipped, errors });
    setStep('result');
    setImporting(false);
    if (inserted + updated > 0) { toast.success(`Import complete: ${inserted} added, ${updated} updated`); onImported?.(); }
  }

  if (!config) return null;

  return (
    <>
      <button className="btn btn-secondary" onClick={openModal} style={{ display:'flex', alignItems:'center', gap:6 }}>
        <Upload size={14}/> Import Excel
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal modal-lg" style={{ maxWidth:740, maxHeight:'90vh', overflowY:'auto' }}>
            <div className="modal-header">
              <span className="modal-title">📥 Import — {config.label}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowModal(false)}><X size={16}/></button>
            </div>
            <div className="modal-body">

              {/* ── STEP 1: UPLOAD ── */}
              {step==='upload' && (
                <div>
                  {/* Download sample */}
                  <div style={{ background:'var(--accent-light)', border:'1px solid var(--accent)', borderRadius:10, padding:'14px 18px', marginBottom:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>Step 1 — Download Sample Format</div>
                    <div style={{ fontSize:12, color:'var(--text3)', marginBottom:12 }}>
                      Download the sample Excel, fill in your data following the format, then upload it below.
                    </div>
                    <button className="btn btn-secondary" onClick={()=>generateSampleExcel(config)}>
                      <Download size={14}/> Download Sample Excel
                    </button>
                  </div>

                  {/* Column guide */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontWeight:700, fontSize:12, marginBottom:8, color:'var(--text2)' }}>Column Guide:</div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:6 }}>
                      {config.columns.map(c=>(
                        <div key={c.field} style={{ background:'var(--bg3)', borderRadius:6, padding:'6px 10px', fontSize:12 }}>
                          <span style={{ fontWeight:600, color: c.required?'var(--red)':'var(--text)' }}>
                            {c.header.replace(' *','')} {c.required&&<span style={{color:'var(--red)'}}>*</span>}
                          </span>
                          {c.hint && <span style={{ color:'var(--text3)', marginLeft:6, fontSize:11 }}>— {c.hint}</span>}
                          {c.type==='date' && <span style={{ color:'var(--text3)', marginLeft:6, fontSize:11 }}>— DD-MM-YYYY</span>}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop:8, fontSize:11, color:'var(--text3)' }}>
                      <span style={{ color:'var(--red)', fontWeight:700 }}>*</span> = Required fields
                    </div>
                  </div>

                  {/* Upload area */}
                  <div style={{ border:'2px dashed var(--border)', borderRadius:10, padding:30, textAlign:'center', cursor:'pointer' }}
                    onClick={()=>fileRef.current?.click()}>
                    <Upload size={32} style={{ color:'var(--accent)', marginBottom:8 }}/>
                    <div style={{ fontWeight:600, marginBottom:4 }}>Click to upload Excel or CSV</div>
                    <div style={{ fontSize:12, color:'var(--text3)' }}>Supports .xlsx, .xls, .csv</div>
                    <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:'none' }} onChange={handleFile}/>
                  </div>

                  {/* Validation errors */}
                  {errors.length>0 && (
                    <div style={{ marginTop:16, background:'#fef2f2', border:'1px solid var(--red)', borderRadius:8, padding:'12px 16px' }}>
                      <div style={{ fontWeight:700, color:'var(--red)', marginBottom:6 }}>⚠️ {errors.length} Validation Errors</div>
                      {errors.slice(0,10).map((e,i)=><div key={i} style={{ fontSize:12, color:'var(--red)' }}>{e}</div>)}
                      {errors.length>10 && <div style={{ fontSize:12, color:'var(--red)' }}>...and {errors.length-10} more</div>}
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP 2: PREVIEW ── */}
              {step==='preview' && (
                <div>
                  {/* Summary */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
                    {[
                      { label:'Total Rows',  value:rows.length,       color:'var(--accent)' },
                      { label:'New Records', value:rows.length-duplicates.length, color:'var(--green)' },
                      { label:'Duplicates',  value:duplicates.length, color:'var(--amber)' },
                    ].map(s=>(
                      <div key={s.label} style={{ background:'var(--bg3)', borderRadius:8, padding:'12px', textAlign:'center' }}>
                        <div style={{ fontSize:11, color:'var(--text3)' }}>{s.label}</div>
                        <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Parse errors */}
                  {errors.length>0 && (
                    <div style={{ background:'#fef2f2', border:'1px solid var(--red)', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
                      <div style={{ fontWeight:700, color:'var(--red)', fontSize:12, marginBottom:4 }}>⚠️ {errors.length} rows skipped due to missing required fields</div>
                      {errors.slice(0,5).map((e,i)=><div key={i} style={{ fontSize:11, color:'var(--red)' }}>{e}</div>)}
                    </div>
                  )}

                  {/* Duplicate handling */}
                  {duplicates.length>0 && (
                    <div style={{ background:'#fffbeb', border:'1.5px solid var(--amber)', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:'#92400e', marginBottom:10 }}>
                        ⚠️ {duplicates.length} Duplicate{duplicates.length>1?'s':''} Found
                      </div>
                      <div style={{ fontSize:12, color:'#78350f', marginBottom:12 }}>
                        These records already exist in the database (matched by <strong>{config.uniqueKey}</strong>):
                      </div>
                      <div style={{ maxHeight:120, overflowY:'auto', marginBottom:12, background:'#fff', borderRadius:6, padding:'6px 10px' }}>
                        {duplicates.map((r,i)=>(
                          <div key={i} style={{ fontSize:12, padding:'3px 0', borderBottom:'1px solid #fde68a' }}>
                            {r[config.uniqueKey]}
                          </div>
                        ))}
                      </div>
                      <div style={{ fontWeight:600, fontSize:12, marginBottom:8 }}>What should we do with duplicates?</div>
                      <div style={{ display:'flex', gap:12 }}>
                        {[
                          { key:'skip',   label:'⏭️ Skip them',          desc:'Keep existing data, ignore duplicates' },
                          { key:'update', label:'🔄 Update existing',    desc:'Overwrite existing records with new data' },
                        ].map(opt=>(
                          <label key={opt.key} style={{ flex:1, cursor:'pointer', background: dupChoice===opt.key?'var(--accent-light)':'var(--bg3)', border:`2px solid ${dupChoice===opt.key?'var(--accent)':'var(--border)'}`, borderRadius:8, padding:'10px 12px' }}>
                            <input type="radio" name="dupChoice" value={opt.key} checked={dupChoice===opt.key} onChange={e=>setDupChoice(e.target.value)} style={{ marginRight:6 }}/>
                            <span style={{ fontWeight:600, fontSize:13 }}>{opt.label}</span>
                            <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>{opt.desc}</div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preview table */}
                  <div style={{ fontWeight:700, fontSize:12, marginBottom:8 }}>Preview — First 10 rows:</div>
                  <div className="table-wrap" style={{ maxHeight:280, overflowY:'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          {config.columns.filter(c=>c.required||['full_name','student_name','college_name','company_name','batch_name','topic_name'].includes(c.field)).slice(0,6).map(c=>(
                            <th key={c.field}>{c.header.replace(' *','')}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0,10).map((r,i)=>{
                          const isDup = duplicates.some(d=>d[config.uniqueKey]===r[config.uniqueKey]);
                          return (
                            <tr key={i} style={{ background: isDup?'#fffbeb':undefined }}>
                              <td style={{ fontSize:11 }}>{i+1} {isDup&&<span style={{ color:'var(--amber)', fontSize:10 }}>DUP</span>}</td>
                              {config.columns.filter(c=>c.required||['full_name','student_name','college_name','company_name','batch_name','topic_name'].includes(c.field)).slice(0,6).map(c=>(
                                <td key={c.field} style={{ fontSize:12 }}>{String(r[c.field]||'—')}</td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {rows.length>10 && <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>...and {rows.length-10} more rows</div>}
                </div>
              )}

              {/* ── STEP 3: RESULT ── */}
              {step==='result' && result && (
                <div style={{ textAlign:'center', padding:'20px 0' }}>
                  <CheckCircle size={48} style={{ color:'var(--green)', marginBottom:12 }}/>
                  <div style={{ fontWeight:800, fontSize:18, marginBottom:16 }}>Import Complete!</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
                    {[
                      { label:'✅ Inserted',  value:result.inserted, color:'var(--green)' },
                      { label:'🔄 Updated',   value:result.updated,  color:'var(--accent)' },
                      { label:'⏭️ Skipped',   value:result.skipped,  color:'var(--amber)' },
                    ].map(s=>(
                      <div key={s.label} style={{ background:'var(--bg3)', borderRadius:8, padding:'14px' }}>
                        <div style={{ fontSize:12, color:'var(--text3)' }}>{s.label}</div>
                        <div style={{ fontSize:28, fontWeight:800, color:s.color }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                  {result.errors?.length>0 && (
                    <div style={{ background:'#fef2f2', border:'1px solid var(--red)', borderRadius:8, padding:'12px', textAlign:'left', marginBottom:12 }}>
                      <div style={{ fontWeight:700, color:'var(--red)', marginBottom:6 }}>⚠️ {result.errors.length} errors during import:</div>
                      {result.errors.map((e,i)=><div key={i} style={{ fontSize:12, color:'var(--red)' }}>{e}</div>)}
                    </div>
                  )}
                  <button className="btn btn-secondary" onClick={()=>{ resetState(); }}>
                    <RefreshCw size={13}/> Import More
                  </button>
                </div>
              )}

            </div>
            <div className="modal-footer">
              {step==='upload' && <button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Close</button>}
              {step==='preview' && (
                <>
                  <button className="btn btn-secondary" onClick={()=>setStep('upload')}>← Back</button>
                  <button className="btn btn-primary" onClick={runImport} disabled={importing||rows.length===0}>
                    {importing ? <><RefreshCw size={13} style={{ animation:'spin 1s linear infinite' }}/> Importing...</> : `📥 Import ${rows.length - (dupChoice==='skip'?duplicates.length:0) + (dupChoice==='update'?duplicates.length:0)} Records`}
                  </button>
                </>
              )}
              {step==='result' && <button className="btn btn-primary" onClick={()=>setShowModal(false)}>Done ✅</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
